// Volume actions (ui-spec §6.11): Delete, Clear and Retry import. Each is the request `nodus delete`, `nodus volume
// clear` and `nodus request reimport` send, and both destructive ones confirm first with the scoped command.
import { newIdempotencyKey, objectPath, requestPatch, subresourcePath, useApi } from "@nodus/api";
import { cliCommand, ConfirmDestructive, usePrefs, type MenuAction } from "@nodus/ui";
import * as React from "react";
import { useRun } from "./parts";
import type { Scope, Volume } from "./model";

type Pending = { action: "delete" | "clear"; volume: Volume } | undefined;

/** A Volume whose import failed can import again; the controller acts on the request annotation. */
export function canRetryImport(v: Volume): boolean {
  return v.status?.phase === "Failed" && v.status.reason === "ImportFailed";
}

export function useVolumeActions(scope: Scope, opts: { onDeleted?: () => void } = {}) {
  const { client } = useApi();
  const prefs = usePrefs();
  const { busy, run } = useRun();
  const [pending, setPending] = React.useState<Pending>();
  const cli = (args: string[]) => cliCommand(args, scope, { omitScope: prefs.omitScope });
  const path = (v: Volume) => objectPath("Volume", v.metadata.name, scope.project);

  const retryImport = (v: Volume) =>
    run(() => client.patch(path(v), requestPatch("reimport"), { org: scope.org }), `Retrying the import of ${v.metadata.name}`);

  const confirm = async (fn: () => Promise<unknown>, done: string, after?: () => void) => {
    if (await run(fn, done)) {
      setPending(undefined);
      after?.();
    }
  };

  const menu = (v: Volume): MenuAction[] => [
    ...(canRetryImport(v) ? [{ label: "Retry import", onSelect: () => void retryImport(v) }] : []),
    { label: "Clear contents", onSelect: () => setPending({ action: "clear", volume: v }) },
    { label: "Delete", destructive: true, separatorBefore: true, onSelect: () => setPending({ action: "delete", volume: v }) },
  ];

  let dialog: React.ReactNode = null;
  if (pending) {
    const v = pending.volume;
    const name = v.metadata.name;
    dialog = (
      <ConfirmDestructive
        open
        onOpenChange={(o) => !o && setPending(undefined)}
        pending={busy}
        {...(pending.action === "clear"
          ? {
              title: `Clear ${name}?`,
              consequences: <p>Commits an empty revision. The Volume and its earlier revisions stay, and new mounts see no files until something writes some.</p>,
              cli: cli(["volume", "clear", name]),
              actionLabel: "Clear contents",
              onConfirm: () =>
                void confirm(
                  () => client.post(subresourcePath("Volume", name, "uploads", scope.project), { mode: "Clear" }, { org: scope.org, idempotencyKey: newIdempotencyKey() }),
                  `Cleared ${name}`,
                ),
            }
          : {
              title: `Delete volume ${name}?`,
              consequences: (
                <p>
                  Every revision and the files in it are deleted for good. Jobs, Sandboxes and Workspaces that mount it cannot start until a Volume with this name exists again. A Volume an
                  attempt is writing to cannot be deleted.
                </p>
              ),
              cli: cli(["delete", `volume/${name}`]),
              confirmText: name,
              actionLabel: "Delete volume",
              onConfirm: () => void confirm(() => client.delete(path(v), { org: scope.org }), `Deleted ${name}`, opts.onDeleted),
            })}
      />
    );
  }
  return { menu, retryImport, dialog, busy };
}
